import { resolvePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Plans — `/finance/plans` (plan-014 §4, design §27.15.1). Owned by plan-014 unit **E4**
 * (FR12-CAT-02, FR12-CAT-04, FR12-CAT-05, FR12-CAT-07, FR12-ENT-08; AC12-08); taken over by plan-020 unit
 * **WAPP-PLAN** (design §32.6, §32.12.3, §32.17; R-APP-2, R-APP-3, R-APP-5, R-APP-6; OD-7, OD-13, OD-14, OD-15, OD-30).
 *
 * Not drawn in Claude Design (OD-15): built from the public plan-card grammar (the Pricing page's `.pp-card`: name and
 * "Recommended" badge, tagline, fee "/ month", "{minutes} minutes included", "Choose {name}", then ticks: the rate per
 * extra minute — "from {lowest}" while the kinds differ —, "Works out at {per} a minute", "{n} calls at once") inside
 * the console styling (ADR 0057), with screenshots for the owner's AP-2 review. The blocks, in order —
 *
 *   held     "Your plan" (`./held-plan.tsx`): the running plan, its period, included minutes used and left, extra
 *            minutes, the next charge, the card and its mandate state; change, cancel at the period end, withdraw.
 *   choice   "Plans": pay as you go first, then the plans in their presentation's order, the current one marked; any
 *            custom offer granted to the organization; the annual plan "Coming soon" while none is sold; "Custom · By
 *            agreement". While sales are closed (`sale_open` false) every plan card says "Coming soon" and cannot be
 *            chosen (OD-14). `?offer=<code>` opens that offer's quote; an unknown or unsellable code shows the chooser.
 *   quote    (`./plan-quote.tsx`) block 4.1 from the frozen quote, the unticked consent, the card form, the outcome.
 *   list     "Add-ons": greyed "Coming soon" rows (owner answer 60), then "Request quota".
 *   list     "Over your plan's limits": what a lowered limit left standing, each with what to do.
 *   notice   a scheduled downgrade or cancellation, or "Downgrades wait for the period to end".
 *
 * Every figure is the API's (R-APP-5): the fee, included minutes, calls at once and rates of an offer from the offers
 * read; nothing here types a price (`no-literal-plan-figure`). Talk time is shown in minutes and seconds (OA-1).
 *
 * WHO SEES WHAT: reading needs `account.finance.read` (Owner, Admin, Finance); quoting, buying, changing and cancelling
 * need `account.finance.manage` (ADR 0033). What a role holds decides what the page OFFERS; the API decides.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { UndocumentedOperationError } from "../../../api/gateway.js";
import { useGateway } from "../../../api/provider.js";
import type { CommercialOfferView, OfferChoiceView } from "../../../api/slice/commercial-offer.js";
import type { BalanceSubscriptionView, EntitlementImpactView, ResourceRestrictionView } from "../../../api/slice/subscription.js";
import { formatMoney } from "../../../money.js";
import { standardTalkRateHeadlineOf, talkRateKindLabel, talkRateText, useStandardTalkRates, type StandardTalkRateSet } from "../../../api/slice/talk-rate.js";
import { GLYPH } from "../../../shell/glyph.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { Block, BlockNote, ItemColumn, ItemRow, Notice, type Outcome } from "../../account-admin/page/organization-page.js";
import { shortDateText } from "../../account-admin/page/role-copy.js";
import { QuotaRequestDialog } from "../../billing/quota-request-dialog.js";
import { HeldPlanBlock, heldNameOf } from "./held-plan.js";
import { extraRateLineOf, feeText, localisedOf, minuteCountText, offerNameOf, rateLinesOf, worksOutPerMinuteOf } from "./plan-copy.js";
import { SubscriptionQuotePanel } from "./plan-quote.js";

import "./plans.css";
import { FinanceTabs } from "../finance-tabs.js";

/** The page's search parameters: `offer` — the code of an offer whose quote opens (`?offer=growth`, OD-14's sign-up carry). */
export interface PlansSearch {
  readonly offer?: string;
}

const OFFER_CODE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL
 * still opens a valid page — a code that is not an offer code's shape is dropped.
 */
export function plansSearchOf(raw: Readonly<Record<string, unknown>>): PlansSearch {
  const offer = raw["offer"];
  return typeof offer === "string" && OFFER_CODE.test(offer) ? { offer } : {};
}

export interface PlansScreenProps {
  readonly search: PlansSearch;
  readonly onSearch: (next: PlansSearch) => void;
}

// ── the words and figures (pure; exported for the suite) ──────────────────────────────────────────────────

function amount(minor: number, currency: string): string {
  return formatMoney(money(minor, currency as CurrencyCode));
}

/** The plan's name, as the plan-014 prototype names a custom or pay-as-you-go offer. */
export function planLabelOf(offer: Pick<CommercialOfferView, "recurrence" | "custom">): string {
  if (offer.custom) return portalText("finance", "m0629");
  switch (offer.recurrence) {
    case "payg":
      return portalText("finance", "m0630");
    case "monthly":
      return portalText("finance", "m0631");
    case "annual":
      return portalText("finance", "m0632");
    case "custom":
      return portalText("finance", "m0629");
  }
}

/**
 * Pay as you go at the STANDARD talk rate a minute, as wetalk.io shows it (owner decision on the AP-2 review, plan-020
 * AP2-FIX; design §32.12.2 B-7's unit): "<rate> / minute" while every kind costs the same, else "from <lowest> / minute" with
 * the kinds' lines under it (`paygRateLinesOf`); "—" while the standard rates are not read. Never the organization's
 * own or plan rate (what `/v1/talk-rates` answers under a plan), never per hour. "€490.00 / month" for a recurring
 * offer. Never "pending". The offer's own `talk_rate_minor_per_minute` is deprecated (PQ-8) and not read.
 */
export function planPriceOf(offer: CommercialOfferView, standard: StandardTalkRateSet | null): string {
  if (offer.recurrence === "payg") {
    const headline = standard === null ? null : standardTalkRateHeadlineOf(standard);
    if (headline === null) return "—";
    return portalText("subscription", headline.from ? "card.per_minute_from" : "card.per_minute", { rate: formatMoney(headline.perMinute) });
  }
  if (offer.recurringChargeMinor === null) return portalText("finance", "m0635");
  const per = offer.recurrence === "annual" ? "year" : "month";
  return `${amount(offer.recurringChargeMinor, offer.currency)} / ${per}`;
}

/** Pay as you go's lines while the standard rates differ by kind ("Web chat: €0.20 a minute"); none while equal. */
export function paygRateLinesOf(offer: CommercialOfferView, standard: StandardTalkRateSet | null): readonly string[] {
  if (offer.recurrence !== "payg" || standard === null || standardTalkRateHeadlineOf(standard)?.from !== true) return [];
  return standard.talkRates.map((one) => portalText("finance", "plans.payg_line", { value0: talkRateKindLabel(one.kind), value1: talkRateText(one.perMinute, "minute") }));
}

/** A custom offer's one-line promise, from its terms — no figure the API did not send. */
export function planDekOf(offer: CommercialOfferView): string {
  if (offer.recurrence === "payg" && !offer.custom) return portalText("finance", "m0636");
  const parts: string[] = [];
  if (offer.included.talkSecond !== null) parts.push(portalText("finance", "m0637", { value1: minuteCountText(offer.included.talkSecond) }));
  if (offer.included.seat !== null) parts.push(`${String(offer.included.seat)} ${offer.included.seat === 1 ? "seat" : "seats"}`);
  parts.push(portalText("finance", "m0638", { value1: String(offer.channelUnit) }));
  const lead = offer.custom ? portalText("finance", "m0639") : offer.recurrence === "annual" ? portalText("finance", "m0640") : portalText("finance", "m0641");
  const until = offer.grantExpiresAt === null ? "" : portalText("finance", "m0642", { value1: shortDateText(offer.grantExpiresAt) });
  return `${lead}: ${parts.join(", ")}.${until}`;
}

/**
 * The current plan: the shared balance's running subscription — its presentation's name when the read carries one —,
 * else pay as you go. (The account overview reads it too.)
 */
export function currentPlanOf(
  offers: readonly CommercialOfferView[],
  subscription: BalanceSubscriptionView | null,
): { readonly label: string; readonly commercialRevisionId: string | null } {
  if (subscription === null) {
    const payg = offers.find((one) => one.recurrence === "payg" && !one.custom);
    return { label: portalText("finance", "m1166"), commercialRevisionId: payg?.commercialRevisionId ?? null };
  }
  if (subscription.displayName !== null) return { label: heldNameOf(subscription), commercialRevisionId: subscription.commercialRevisionId };
  const offer = offers.find((one) => one.commercialRevisionId === subscription.commercialRevisionId);
  const label = offer === undefined ? (subscription.recurrence === "annual" ? portalText("finance", "m0632") : subscription.recurrence === "monthly" ? portalText("finance", "m0631") : portalText("finance", "m0629")) : planLabelOf(offer);
  return { label, commercialRevisionId: subscription.commercialRevisionId };
}

const KIND_WORD: Readonly<Record<string, { readonly one: string; readonly many: string }>> = {
  voice_agent: { get one() { return portalText("finance", "m1167"); }, get many() { return portalText("finance", "m1168"); } },
  phone_number: { get one() { return portalText("finance", "m1169"); }, get many() { return portalText("finance", "m1170"); } },
  workspace: { get one() { return portalText("finance", "m1171"); }, get many() { return portalText("finance", "m1172"); } },
  seat: { get one() { return portalText("finance", "m1173"); }, get many() { return portalText("finance", "m1174"); } },
  recording_storage: { get one() { return portalText("finance", "m1175"); }, get many() { return portalText("finance", "m1176"); } },
  channel_unit: { get one() { return portalText("finance", "m1177"); }, get many() { return portalText("finance", "m1178"); } },
};

function counted(count: number, kind: string): string {
  switch (kind) {
    case "voice_agent": return portalText("finance", "plan.count.voice_agent", { count: count });
    case "phone_number": return portalText("finance", "plan.count.phone_number", { count: count });
    case "workspace": return portalText("finance", "plan.count.workspace", { count: count });
    case "seat": return portalText("finance", "plan.count.seat", { count: count });
    case "recording_storage": return portalText("finance", "plan.count.recording_storage", { count: count });
    case "channel_unit": return portalText("finance", "plan.count.channel_unit", { count: count });
    default: return `${String(count)} ${kind}`;
  }
}

/** One line of the plan-014 over-limit preview, in plain words. */
export function impactLineText(line: EntitlementImpactView["line"][number]): string {
  const names = line.affected.map((one) => one.name).join(", ");
  switch (line.restriction) {
    case "paused":
      return portalText("finance", "m0647", { value1: counted(line.used, line.resourceKind), value2: String(line.limitAfter), value3: names });
    case "read_only":
      return portalText("finance", "m0648", { value1: counted(line.used, line.resourceKind), value2: String(line.limitAfter), value3: names });
    case "no_new_storage":
      return portalText("finance", "m0649");
    case "no_new":
      return portalText("finance", "m0650", { value1: counted(line.used, line.resourceKind), value2: String(line.limitAfter), value3: KIND_WORD[line.resourceKind]?.many ?? line.resourceKind });
  }
}

/** What a standing restriction says, and what the person can do about it. */
export function restrictionCopyOf(one: ResourceRestrictionView): { readonly title: string; readonly dek: string; readonly to: string | null; readonly action: string | null } {
  const name = one.resourceName ?? "";
  switch (one.resourceKind) {
    case "voice_agent":
      return one.restriction === "paused"
        ? { title: name === "" ? portalText("finance", "m0651") : portalText("finance", "m0652", { value1: name }), dek: portalText("finance", "m0653"), to: "/agents", action: portalText("finance", "m0654") }
        : { title: portalText("finance", "m0655"), dek: portalText("finance", "m0656"), to: "/agents", action: portalText("finance", "m0654") };
    case "phone_number":
      return one.restriction === "read_only"
        ? { title: name === "" ? portalText("finance", "m0657") : portalText("finance", "m0658", { value1: name }), dek: portalText("finance", "m0659"), to: "/numbers", action: portalText("finance", "m0660") }
        : { title: portalText("finance", "m0661"), dek: portalText("finance", "m0662"), to: "/numbers", action: portalText("finance", "m0660") };
    case "seat":
      return { title: portalText("finance", "m0663"), dek: portalText("finance", "m0664"), to: "/organization/people", action: portalText("finance", "m0665") };
    case "recording_storage":
      return { title: portalText("finance", "m0666"), dek: portalText("finance", "m0667"), to: null, action: null };
    case "workspace":
      return { title: portalText("finance", "m0668"), dek: portalText("finance", "m0669"), to: "/workspaces", action: portalText("finance", "m0670") };
    case "channel_unit":
      return { title: portalText("finance", "m0671"), dek: portalText("finance", "m0672"), to: null, action: null };
  }
}

/** The plans in the chooser's order: pay as you go, then the presentation's order, then custom offers. */
export function choicesInSequence(offers: readonly OfferChoiceView[]): readonly OfferChoiceView[] {
  const payg = offers.filter((one) => one.recurrence === "payg" && !one.custom);
  const bySortPosition = (a: OfferChoiceView, b: OfferChoiceView): number => (a.presentation?.displayOrder ?? 0) - (b.presentation?.displayOrder ?? 0);
  const monthly = offers.filter((one) => one.presentation !== null && one.billingPeriod === "monthly").slice().sort(bySortPosition);
  const annual = offers.filter((one) => one.presentation !== null && one.billingPeriod === "annual").slice().sort(bySortPosition);
  const shown = [...monthly, ...annual];
  const custom = offers.filter((one) => one.custom);
  return [...payg, ...shown, ...custom];
}

/** The offer `?offer=<code>` names, when it is one this organization may be quoted now; else `null` (the chooser). */
export function offerOfSearch(offers: readonly OfferChoiceView[], code: string | undefined, saleOpen: boolean, currentRevisionId: string | null): OfferChoiceView | null {
  if (code === undefined) return null;
  const found = offers.find((one) => one.offerCode === code && one.sellable && one.recurrence !== "payg");
  if (found === undefined || found.commercialRevisionId === currentRevisionId) return null;
  if (!saleOpen && !found.custom) return null;
  return found;
}

// ── the page frame ───────────────────────────────────────────────────────────────────────────────────────

function PlansPage(props: { readonly outcome: Outcome | null; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("finance");
  usePortalTranslation("subscription");
  return (
    <section className="op-page">
      <h1 className="op-title">{portalText("finance", "m0673")}</h1>
      <p className="op-lede">{portalText("subscription", "page.lede")}</p>
      <FinanceTabs current="plans" />
      {props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
      {props.children}
    </section>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────

/** Owner answer 60: the prototype's four add-ons, greyed as "Coming soon" — no price, no action. */
const COMING_SOON_ADD_ON = [
  { glyph: GLYPH.arrowsSplit, get title() { return portalText("finance", "m0684"); }, get dek() { return portalText("finance", "m1240"); } },
  { glyph: GLYPH.hardDrives, get title() { return portalText("finance", "m1241"); }, get dek() { return portalText("finance", "m1242"); } },
  { glyph: GLYPH.personPlus, get title() { return portalText("finance", "m1243"); }, get dek() { return portalText("finance", "m1244"); } },
  { glyph: GLYPH.lifebuoy, get title() { return portalText("finance", "m1245"); }, get dek() { return portalText("finance", "m1246"); } },
] as const;

/** A greyed add-on row: the `list` block's row, dimmed, tagged "Coming soon", with no price and no action. */
function ComingSoonRow(props: { readonly glyph: (typeof COMING_SOON_ADD_ON)[number]["glyph"]; readonly title: string; readonly dek: string }): ReactNode {
  usePortalTranslation("finance");
  return (
    <div className="op-item pl-soon" aria-disabled="true">
      <span className="op-item-icon op-tone-neutral">
        <Icon glyph={props.glyph} size="lg" />
      </span>
      <span className="op-item-body">
        <span className="op-item-head">
          <span className="op-item-title">{props.title}</span>
          <span className="tag tag-neutral op-item-tag">{portalText("finance", "m0677")}</span>
        </span>
        <span className="op-item-dek">{props.dek}</span>
      </span>
    </div>
  );
}

export function PlansScreen(props: PlansScreenProps): ReactNode {
  usePortalTranslation("finance");
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [quotaOpen, setQuotaOpen] = useState(false);

  const enabled = accountId !== null && canRead;
  const offers = useQuery({ queryKey: ["offer-choices", accountId ?? "none"], queryFn: () => gateway.listOfferChoices(accountId ?? ""), enabled });
  const balances = useQuery({ queryKey: ["billing-accounts", accountId ?? "none"], queryFn: () => gateway.listBillingAccounts(accountId ?? ""), enabled });
  const shared = balances.data?.find((one) => one.scope === "account") ?? null;
  const subscription = useQuery({
    queryKey: ["balance-subscription", shared?.billingAccountId ?? "none"],
    queryFn: () => gateway.readBalanceSubscription(shared?.billingAccountId ?? ""),
    enabled: enabled && shared !== null,
  });
  const restrictions = useQuery({
    queryKey: ["resource-restrictions", accountId ?? "none"],
    queryFn: () => gateway.listResourceRestrictions(accountId ?? ""),
    enabled,
    retry: false,
  });

  if (open.loading || (enabled && (offers.isPending || balances.isPending || (shared !== null && subscription.isPending)))) {
    return (
      <PlansPage outcome={null}>
        <BlockNote>{portalText("finance", "m0678")}</BlockNote>
      </PlansPage>
    );
  }
  if (accountId === null || !canRead) {
    return (
      <PlansPage outcome={null}>
        <BlockNote>{portalText("finance", "m0679")}</BlockNote>
      </PlansPage>
    );
  }
  if (offers.isError || balances.isError || subscription.isError) {
    const failed = offers.isError ? offers.error : balances.isError ? balances.error : subscription.error;
    return (
      <PlansPage outcome={null}>
        <div className="pl-actions">
          <BlockNote>{apiProblemText(failed, portalText("finance", "m0680"))}</BlockNote>
          <Button
            variant="secondary"
            onClick={() => {
              void offers.refetch();
              void balances.refetch();
              void subscription.refetch();
            }}
          >{portalText("finance", "m0382")}</Button>
        </div>
      </PlansPage>
    );
  }

  const choice = offers.data ?? { offer: [], saleOpen: false };
  const held = subscription.data ?? null;
  const current = currentPlanOf(choice.offer, held);
  const quoted = canManage && shared !== null ? offerOfSearch(choice.offer, props.search.offer, choice.saleOpen, current.commercialRevisionId) : null;
  const showOutcome = (next: Outcome): void => {
    setOutcome(next);
    props.onSearch({});
  };

  if (quoted !== null && shared !== null) {
    return (
      <PlansPage outcome={outcome}>
        <SubscriptionQuotePanel
          key={quoted.commercialRevisionId}
          accountId={accountId}
          billingAccountId={shared.billingAccountId}
          offer={quoted}
          held={held}
          onBack={() => props.onSearch({})}
          onDone={showOutcome}
        />
      </PlansPage>
    );
  }

  return (
    <PlansPage outcome={outcome}>
      {held === null ? null : (
        <HeldPlanBlock
          accountId={accountId}
          held={held}
          canManage={canManage}
          onChange={() => document.getElementById("plan-choice")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          onOutcome={setOutcome}
        />
      )}
      <PlanChoiceBlock
        offers={choice.offer}
        saleOpen={choice.saleOpen}
        current={current.commercialRevisionId}
        hasShared={shared !== null}
        canManage={canManage}
        onChoose={(code) => {
          setOutcome(null);
          props.onSearch({ offer: code });
        }}
      />
      <Block title={portalText("finance", "m0681")} dek={portalText("finance", "m0682")}>
        <ItemColumn label={portalText("finance", "m0683")}>
          {COMING_SOON_ADD_ON.map((one) => (
            <ComingSoonRow key={one.title} glyph={one.glyph} title={one.title} dek={one.dek} />
          ))}
        </ItemColumn>
        <ItemColumn label={portalText("finance", "m0684")}>
          <ItemRow
            glyph={GLYPH.usersThree}
            tone="neutral"
            title={portalText("finance", "m0685")}
            tag={null}
            dek={portalText("finance", "m0686")}
            actions={
              <Button variant="secondary" onClick={() => setQuotaOpen(true)}>{portalText("finance", "m0687")}</Button>
            }
          />
        </ItemColumn>
      </Block>
      <RestrictionBlock restrictions={restrictions.data ?? null} error={restrictions.isError ? restrictions.error : null} />
      <ScheduledChangeNotice subscription={held} offers={choice.offer} />
      <QuotaRequestDialog open={quotaOpen} onClose={() => setQuotaOpen(false)} currentCeiling={null} />
    </PlansPage>
  );
}

// ── "Plans" ────────────────────────────────────────────────────────────────────────────────────────────────

const CUSTOM_CHOICE = "custom-by-agreement";

/** One plan card: the public card grammar inside the console (OD-15). */
function OfferCard(props: {
  readonly offer: OfferChoiceView;
  readonly isCurrent: boolean;
  readonly saleOpen: boolean;
  readonly canChoose: boolean;
  readonly paygRates: StandardTalkRateSet | null;
  readonly onChoose: () => void;
}): ReactNode {
  usePortalTranslation("subscription");
  usePortalTranslation("finance");
  const { offer } = props;
  const payg = offer.recurrence === "payg" && !offer.custom;
  const name = offerNameOf(offer);
  const recommended = offer.presentation?.recommended === true;
  const soon = !payg && !offer.custom && !props.saleOpen;
  const unavailable = !payg && !offer.sellable;
  const fee = offer.recurringChargeMinor === null ? null : money(offer.recurringChargeMinor, offer.currency as CurrencyCode);
  // "Works out at" is a monthly fee over a month's minutes; an annual fee is not divided here.
  const per = fee === null || offer.billingPeriod !== "monthly" ? null : worksOutPerMinuteOf(fee, offer.includedTalkSecond);
  const extra = extraRateLineOf(offer.resolvedTalkRates);
  const unequal = offer.resolvedTalkRates.some((one) => one.perMinute.amountMinor !== offer.resolvedTalkRates[0]?.perMinute.amountMinor);
  const classes = ["pl-card", recommended ? "pl-card-recommended" : "", props.isCurrent ? "pl-card-current" : "", soon || unavailable ? "pl-card-soon" : ""].filter((one) => one !== "").join(" ");
  return (
    <article className={classes} aria-label={name} data-offer={offer.offerCode} aria-disabled={soon || unavailable ? "true" : undefined}>
      <div className="pl-card-nameline">
        <h3 className="pl-card-name">{name}</h3>
        {recommended ? <span className="pl-card-badge">{portalText("subscription", "card.recommended")}</span> : null}
        {props.isCurrent ? <span className="tag tag-accent pl-option-tag">{portalText("subscription", "card.current")}</span> : null}
        {soon ? <span className="tag tag-neutral pl-option-tag">{portalText("subscription", "chooser.soon_title")}</span> : null}
      </div>
      {offer.presentation !== null ? <p className="pl-card-who">{localisedOf(offer.presentation.tagline)}</p> : <p className="pl-card-who">{planDekOf(offer)}</p>}
      <p className="pl-card-price">
        {payg ? planPriceOf(offer, props.paygRates) : fee === null ? portalText("finance", "m0635") : portalText("subscription", offer.recurrence === "annual" ? "card.per_year" : "card.per_month", { fee: feeText(fee) })}
      </p>
      {!payg && offer.includedTalkSecond > 0 ? <p className="pl-card-minline">{portalText("subscription", "card.included", { incl: minuteCountText(offer.includedTalkSecond) })}</p> : null}
      {payg ? paygRateLinesOf(offer, props.paygRates).map((line) => <p key={line} className="pl-card-minline">{line}</p>) : null}
      <div className="pl-card-action">
        {props.isCurrent || payg ? null : soon ? (
          <Button variant="secondary" disabled>{portalText("subscription", "chooser.soon_title")}</Button>
        ) : unavailable ? (
          <p className="pl-card-note">{portalText("subscription", "card.unavailable")}</p>
        ) : props.canChoose ? (
          <Button variant={recommended ? "primary" : "secondary"} onClick={props.onChoose}>
            {portalText("subscription", "card.choose", { name })}
          </Button>
        ) : null}
      </div>
      {payg ? null : (
        <ul className="pl-card-ticks">
          {extra === null ? null : <li>{extra}</li>}
          {per === null ? null : <li>{portalText("subscription", "card.works_out", { per: formatMoney(per) })}</li>}
          <li>{portalText("subscription", "card.calls_at_once", { conc: String(offer.channelUnit) })}</li>
        </ul>
      )}
      {!payg && unequal ? (
        <ul className="pl-card-kinds">
          {rateLinesOf(offer.resolvedTalkRates).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function PlanChoiceBlock(props: {
  readonly offers: readonly OfferChoiceView[];
  readonly saleOpen: boolean;
  readonly current: string | null;
  readonly hasShared: boolean;
  readonly canManage: boolean;
  readonly onChoose: (offerCode: string) => void;
}): ReactNode {
  usePortalTranslation("subscription");
  usePortalTranslation("finance");
  // plan-020 AP2-FIX: pay as you go is priced at the STANDARD rate, as on wetalk.io — never the organization's own.
  const rates = useStandardTalkRates().data ?? null;
  const [customAsked, setCustomAsked] = useState(false);
  const ordered = choicesInSequence(props.offers);
  const annualListed = props.offers.some((one) => one.billingPeriod === "annual" && one.presentation !== null);
  const canChoose = props.canManage && props.hasShared;
  return (
    <Block title={portalText("subscription", "chooser.title")}>
      <div id="plan-choice" />
      {props.saleOpen ? null : (
        <p className="pl-note pl-soon-note" role="status">
          <strong>{portalText("subscription", "chooser.soon_title")}</strong> <span>{portalText("subscription", "chooser.soon_text")}</span>
        </p>
      )}
      {props.canManage && !props.hasShared ? <p className="pl-note">{portalText("subscription", "chooser.no_shared_balance")}</p> : null}
      {!props.canManage ? <p className="pl-note">{portalText("subscription", "chooser.read_only")}</p> : null}
      <div className="pl-cards">
        {ordered.map((one) => (
          <OfferCard
            key={one.commercialRevisionId}
            offer={one}
            isCurrent={one.commercialRevisionId === props.current}
            saleOpen={props.saleOpen}
            canChoose={canChoose}
            paygRates={rates}
            onChoose={() => props.onChoose(one.offerCode)}
          />
        ))}
        {annualListed ? null : (
          <article className="pl-card pl-card-soon" aria-label={portalText("finance", "m0632")} aria-disabled="true">
            <div className="pl-card-nameline">
              <h3 className="pl-card-name">{portalText("finance", "m0632")}</h3>
              <span className="tag tag-neutral pl-option-tag">{portalText("finance", "m0677")}</span>
            </div>
            <p className="pl-card-who">{portalText("finance", "m0676")}</p>
          </article>
        )}
        <article className="pl-card" aria-label={portalText("finance", "m0629")} data-offer={CUSTOM_CHOICE}>
          <div className="pl-card-nameline">
            <h3 className="pl-card-name">{portalText("finance", "m0629")}</h3>
          </div>
          <p className="pl-card-who">{portalText("finance", "m0692")}</p>
          <p className="pl-card-price">{portalText("finance", "m0635")}</p>
          <div className="pl-card-action">
            <Button variant="secondary" onClick={() => setCustomAsked(true)}>{portalText("finance", "m0629")}</Button>
          </div>
        </article>
      </div>
      {customAsked ? (
        <p className="pl-note" role="status">{portalText("finance", "m0693")}</p>
      ) : null}
    </Block>
  );
}

// ── "Over your plan's limits" ─────────────────────────────────────────────────────────────────────────────

function RestrictionBlock(props: { readonly restrictions: readonly ResourceRestrictionView[] | null; readonly error: unknown }): ReactNode {
  usePortalTranslation("finance");
  if (props.error !== null) {
    // Until the next client regeneration the read is not reachable yet: say so rather than show "none".
    if (props.error instanceof UndocumentedOperationError) return null;
    return (
      <Block title={portalText("finance", "m0707")}>
        <BlockNote>{apiProblemText(props.error, portalText("finance", "m0708"))}</BlockNote>
      </Block>
    );
  }
  if (props.restrictions === null || props.restrictions.length === 0) return null;
  return (
    <Block title={portalText("finance", "m0707")} dek={portalText("finance", "m0709")}>
      <ItemColumn label={portalText("finance", "m0707")}>
        {props.restrictions.map((one) => {
          const copy = restrictionCopyOf(one);
          return (
            <ItemRow
              key={one.resourceRestrictionId}
              glyph={one.restriction === "paused" ? GLYPH.pause : GLYPH.warning}
              tone="neutral"
              title={copy.title}
              tag={portalText("finance", "m0710", { value1: shortDateText(one.disclosedAt) })}
              dek={copy.dek}
              actions={
                copy.to === null || copy.action === null ? undefined : (
                  <Link to={copy.to} className="btn btn-secondary">
                    {copy.action}
                  </Link>
                )
              }
            />
          );
        })}
      </ItemColumn>
    </Block>
  );
}

// ── "Downgrades wait for the period to end" ───────────────────────────────────────────────────────────────

function ScheduledChangeNotice(props: { readonly subscription: BalanceSubscriptionView | null; readonly offers: readonly OfferChoiceView[] }): ReactNode {
  usePortalTranslation("finance");
  const change = props.subscription?.scheduledChange.find((one) => one.kind === "downgrade" || one.kind === "cancellation") ?? null;
  if (change !== null) {
    const target = props.offers.find((one) => one.commercialRevisionId === change.targetCommercialRevisionId);
    const to = change.kind === "cancellation" ? portalText("finance", "m0711") : target === undefined ? portalText("finance", "m0712") : offerNameOf(target);
    return (
      <Notice
        tone="neutral"
        glyph={GLYPH.calendar}
        title={portalText("finance", "m0713", { value1: to, value2: shortDateText(change.effectiveAt) })}
        text={portalText("finance", "m0714")}
      />
    );
  }
  return (
    <Notice
      tone="neutral"
      glyph={GLYPH.calendar}
      title={portalText("finance", "m0715")}
      text={portalText("finance", "m0716")}
    />
  );
}
